import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { NgModule } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { HttpClientModule, HTTP_INTERCEPTORS } from "@angular/common/http";
import { RouterModule } from "@angular/router";
import { AppComponent } from "./app.component";
import { NgbModule } from "@ng-bootstrap/ng-bootstrap";
import { AppRoutingModule } from "./app.routing";
import { ComponentsModule } from "./components/components.module";
import { DepartmentComponent } from "./pages/department/department.component";
import { MyProfileComponent } from "./pages/my-profile/my-profile.component";
import { ProDashboardComponent } from "./pages/pro-dashboard/pro-dashboard.component";
import { ForgotPasswordComponent } from "./pages/forgot-password/forgot-password.component";
import { UpdatePasswordComponent } from "./pages/update-password/update-password.component";
import { ResetPasswordComponent } from "./pages/reset-password/reset-password.component";
import { NgOtpInputModule } from "ng-otp-input";
import { GlobalService } from "./services/global.service";
import { CommonService } from "./services/common.service";
import { AdminGuard } from "./auth-guard/admin.guard";
import { HttpTokenInterceptor } from "./http.interceptor";
import { APP_CONFIG, IAppConfig } from "./app.config";
import { DatePipe } from "@angular/common";
import { CourseComponent } from "./pages/course/course.component";
import { SubjectComponent } from "./pages/subject/subject.component";
import { BatchComponent } from "./pages/batch/batch.component";
import { ClassroomComponent } from "./pages/classroom/classroom.component";
import { CameraComponent } from "./pages/camera/camera.component";
import { AudioDeviceComponent } from "./pages/audio-device/audio-device.component";
import { RaspberryDeviceComponent } from "./pages/raspberry-device/raspberry-device.component";
import { HardwareMappingComponent } from "./pages/hardware-mapping/hardware-mapping.component";
import { DatepickerModule, BsDatepickerModule } from "ngx-bootstrap/datepicker";
import { NgSelectModule } from "@ng-select/ng-select";
import { VendorManagementComponent } from "./pages/vendor-management/vendor-management.component";
import { ProfessorManagementComponent } from "./pages/professor-management/professor-management.component";
import { StudentManagementComponent } from "./pages/student-management/student-management.component";
import { RoundPipe } from "./pipe-components/round.pipe";
import { StudentVideoSearchComponent } from "./pages/student-video-search/student-video-search.component";
import { ProfessorVideoSearchComponent } from "./pages/professor-video-search/professor-video-search.component";
import { PendingApprovalComponent } from "./pages/pending-approval/pending-approval.component";
import { CUSTOM_ELEMENTS_SCHEMA } from "@angular/core";
import { CommonModule } from "@angular/common";
import { LoginComponent } from "./pages/login/login.component";
import { ChangePasswordComponent } from "./pages/change-password/change-password.component";
import { DashboardComponent } from "./pages/dashboard/dashboard.component";
import { ScheduleComponent } from "./pages/schedule/schedule.component";
import { UserManagementComponent } from "./pages/user-management/user-management.component";
import { AdminSidebarComponent } from "./components/adminsidebar/adminsidebar.component";
import { SuperAdminSidebarComponent } from "./components/super-admin-sidebar/super-admin-sidebar.component";
import { GeneralNavbarComponent } from "./components/general-navbar/general-navbar.component";
import { SidebarComponent } from "./components/sidebar/sidebar.component";
import { NavbarComponent } from "./components/navbar/navbar.component";
import { FooterComponent } from "./components/footer/footer.component";
import { FileServerComponent } from "./pages/file-server/file-server.component";
import { VideoListComponent } from "./pages/report-menu/video-list/video-list.component";
import { ScheduleHistoryComponent } from "./pages/report-menu/schedule-history/schedule-history.component";
import { VideoDetailsComponent } from "./pages/video-details/video-details.component";
import { NgxPaginationModule } from "ngx-pagination";
import { ProfessorUpcomingScheduleComponent } from "./pages/professor-upcoming-schedule/professor-upcoming-schedule.component";
import { StudentExpiredComponent } from "./pages/report-menu/student-expired/student-expired.component";
import { PlyrModule } from "ngx-plyr";
import { NgxSpinnerModule } from "ngx-spinner";
import { PrivacyPolicyComponent } from "./pages/privacy-policy/privacy-policy.component";
import { VideocutplayComponent } from "./components/videocutplay/videocutplay.component";
import { PlayvideoComponent } from "./pages/playvideo/playvideo.component";
import { LiveclassComponent } from "./pages/liveclass/liveclass.component";
import { LiveclasslistComponent } from "./pages/liveclasslist/liveclasslist.component";
import { LiveprofessorclassComponent } from "./pages/liveprofessorclass/liveprofessorclass.component";
import { VideoReportComponent } from "./pages/report-menu/video-report/video-report.component";
import { VideoReportsNewlistComponent } from "./pages/report-menu/video-reports-newlist/video-reports-newlist.component";
import { AudioReportComponent } from "./pages/report-menu/audio-report/audio-report.component";
import { HealthCheckupComponent } from "./pages/health-checkup/health-checkup.component";
import { ReportLoginComponent } from "./pages/report-menu/report-login/report-login.component";
import { WINDOW_PROVIDERS } from "./window.providers";
import { SummaryReportComponent } from "./pages/report-menu/summary-report/summary-report.component";
import { DailyReportComponent } from "./pages/report-menu/daily-report/daily-report.component";
import { NonaudibleRecordsComponent } from "./pages/report-menu/nonaudible-records/nonaudible-records.component";
import { MaterialModule } from "./material/material/material.module";
import { OutloginplayvideoComponent } from "./pages/outloginplayvideo/outloginplayvideo.component";

import {
  VgCoreModule,
  VgControlsModule,
  VgOverlayPlayModule,
  VgBufferingModule,
  VgStreamingModule,
} from "ngx-videogular";
import { NoRightClickDirective } from "./directives/no-right-click.directive";
import { RangeSliderDirective } from "./directives/range-slider.directive";
import { CommentSectionComponent } from "./pages/comment-section/comment-section.component";
import { SummaryPopupComponent } from "./pages/report-menu/summary-popup/summary-popup.component";
import { StaffUsageReportComponent } from "./pages/report-menu/staff-usage-report/staff-usage-report.component";
import { DetailedStaffUsageReportComponent } from "./pages/report-menu/detailed-staff-usage-report/detailed-staff-usage-report.component";
import { MatRadioModule } from "@angular/material/radio";
import { ErrorModalComponent } from "./pages/error-modal/error-modal.component";
import { StaffUsagePopupComponent } from "./pages/report-menu/staff-usage-popup/staff-usage-popup.component";
import { NonapprovalReportComponent } from "./pages/report-menu/nonapproval-report/nonapproval-report.component";
import { MyDepartmentClassesComponent } from "./pages/my-department-classes/my-department-classes.component";
import { SessionMasterComponent } from "./pages/session-master/session-master.component";
import { SharedModule } from "./shared/shared.module";
import { VideoBookmarkComponent } from './pages/video-bookmark/video-bookmark.component';
import { UpdateRoleModalComponent } from './pages/update-role-modal/update-role-modal.component';
import { DownloadReportComponent } from './pages/download-report/download-report.component';
import { TextSearchInVideoComponent } from './pages/text-search-in-video/text-search-in-video.component';
import { HighlightPipe } from './pipe-components/highlight.pipe';
import { ApprovedVideosComponent } from './pages/approved-videos/approved-videos.component';
import { UnapprovalVideosComponent } from './pages/unapproval-videos/unapproval-videos.component';

import {MatTabsModule} from '@angular/material/tabs';
import { TopTenStudentViewedlistComponent } from './pages/top-ten-student-viewedlist/top-ten-student-viewedlist.component';
import { TopTenViewedVideosComponent } from './pages/top-ten-viewed-videos/top-ten-viewed-videos.component';
import { DepartmentSummaryComponent } from './pages/report-menu/department-summary/department-summary.component';
import { StudentBookmarkComponent } from './pages/student-bookmark/student-bookmark.component';

@NgModule({
  imports: [
    BrowserAnimationsModule,
    NgxPaginationModule,
    FormsModule,
    HttpClientModule,
    ComponentsModule,
    NgbModule,
    RouterModule,
    AppRoutingModule,
    NgOtpInputModule,
    ReactiveFormsModule,
    NgSelectModule,
    BrowserModule,
    BsDatepickerModule.forRoot(),
    DatepickerModule.forRoot(),
    CommonModule,
    PlyrModule,
    NgxSpinnerModule,
    NgbModule,
    MaterialModule,
    VgCoreModule,
    VgControlsModule,
    VgOverlayPlayModule,
    VgBufferingModule,
    VgStreamingModule,
    MatRadioModule,
    SharedModule,
    MatTabsModule,
  ],

  declarations: [
    AppComponent,
    DepartmentComponent,
    MyProfileComponent,
    ProDashboardComponent,
    ForgotPasswordComponent,
    UpdatePasswordComponent,
    ResetPasswordComponent,
    CourseComponent,
    SubjectComponent,
    BatchComponent,
    ClassroomComponent,
    CameraComponent,
    AudioDeviceComponent,
    RaspberryDeviceComponent,
    HardwareMappingComponent,
    VendorManagementComponent,
    ProfessorManagementComponent,
    StudentManagementComponent,
    RoundPipe,
    StudentVideoSearchComponent,
    ProfessorVideoSearchComponent,
    PendingApprovalComponent,
    LoginComponent,
    ChangePasswordComponent,
    MyProfileComponent,
    AdminSidebarComponent,
    DashboardComponent,
    ScheduleComponent,
    UserManagementComponent,
    FooterComponent,
    NavbarComponent,
    SidebarComponent,
    GeneralNavbarComponent,
    SuperAdminSidebarComponent,
    AdminSidebarComponent,
    FileServerComponent,
    VideoListComponent,
    ScheduleHistoryComponent,
    VideoDetailsComponent,
    ProfessorUpcomingScheduleComponent,
    PrivacyPolicyComponent,
    StudentExpiredComponent,
    VideocutplayComponent,
    CommentSectionComponent,
    PlayvideoComponent,
    LiveclassComponent,
    LiveclasslistComponent,
    LiveprofessorclassComponent,
    VideoReportComponent,
    VideoReportsNewlistComponent,
    OutloginplayvideoComponent,
    AudioReportComponent,
    HealthCheckupComponent,
    ReportLoginComponent,
    SummaryReportComponent,
    DailyReportComponent,
    NonaudibleRecordsComponent,
    NoRightClickDirective,
    SummaryPopupComponent,
    StaffUsageReportComponent,
    DetailedStaffUsageReportComponent,
    ErrorModalComponent,
    StaffUsagePopupComponent,
    NonapprovalReportComponent,
    MyDepartmentClassesComponent,
    SessionMasterComponent,
    RangeSliderDirective,
    VideoBookmarkComponent,
    UpdateRoleModalComponent,
    DownloadReportComponent,
    TextSearchInVideoComponent,
    HighlightPipe,
    ApprovedVideosComponent,
    UnapprovalVideosComponent,
    TopTenStudentViewedlistComponent,
    TopTenViewedVideosComponent,
    DepartmentSummaryComponent,
    StudentBookmarkComponent,
  ],
  providers: [
    { provide: APP_CONFIG, useValue: IAppConfig },

    { provide: HTTP_INTERCEPTORS, useClass: HttpTokenInterceptor, multi: true },
    GlobalService,
    CommonService,
    AdminGuard,
    DatePipe,
    WINDOW_PROVIDERS,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  bootstrap: [AppComponent],
})
export class AppModule {}
